import React, { memo } from "react";

import { isEqual } from "lodash/fp";

import Typography from "@mui/material/Typography";

import TableCell from "@app/analytics/components/Table/TableCell";
import SourceCell from "@app/analytics/MeetingReports/ReportsTable/cells/SourceCell";
import DateAndTimeCell from "@app/analytics/MeetingReports/ReportsTable/MeetingReportTableRow/DateAndTimeCell";
import MeetingTitleCell from "@app/analytics/MeetingReports/ReportsTable/MeetingReportTableRow/MeetingTitleCell";
import { ReportTableRow } from "@app/analytics/MeetingReports/ReportsTable/styles";
import useResponsiveView from "@app/common/hooks/useResponsiveView";
import type { UserMeeting } from "@app/common/types";

import Link from "@rui/Link";

import {
  getIncompleteDetails,
  getIncompleteReason,
  getIncompleteReasonDisplayValue,
  IncompleteDetailsWithFix,
} from "./incompleteReasonDetails";
import { CHIP_STYLE, ReasonChip } from "./styles";

const IncompleteReportTableRow = ({
  report,
  onOpenDetails,
  unseen = false,
}: {
  report: UserMeeting;
  onOpenDetails: (details: IncompleteDetailsWithFix) => void;
  unseen?: boolean;
}) => {
  const { inDesktopView } = useResponsiveView();
  const incompleteDetails = getIncompleteDetails(report);
  const reason = getIncompleteReason(report);

  const reasonChip = (
    <ReasonChip sx={CHIP_STYLE[reason]}>
      {getIncompleteReasonDisplayValue(reason)}
    </ReasonChip>
  );

  const details = (
    <>
      {incompleteDetails.map((detail) => (
        <Typography
          sx={{ textWrap: "wrap" }}
          key={detail.message}
          variant="bodySecondaryReg"
        >
          {detail.message}{" "}
          {"fix" in detail && (
            <Link
              onClick={(e) => {
                e.preventDefault();
                onOpenDetails(detail);
              }}
              underline
            >
              Details.
            </Link>
          )}
        </Typography>
      ))}
    </>
  );

  return (
    <ReportTableRow
      data-test-id="meeting-report-table-row"
      data-sid={report.id}
      unseen={unseen}
    >
      <SourceCell report={report} />
      <MeetingTitleCell
        report={report}
        suppressAlerts
        meetingTitleCellDenseContent={{
          reason: reasonChip,
          details,
        }}
      />
      {inDesktopView && (
        <>
          <DateAndTimeCell report={report} />
          <TableCell>
            <Typography variant="bodySecondaryReg">
              {report.ownerIsCurrentUser ? "Report owner" : "Participant"}
            </Typography>
          </TableCell>
          <TableCell>{reasonChip}</TableCell>
          <TableCell sx={{ whiteSpace: "wrap", maxWidth: "280px" }}>
            {details}
          </TableCell>
        </>
      )}
    </ReportTableRow>
  );
};

const MemoizedIncompleteReportTableRow = memo(
  IncompleteReportTableRow,
  isEqual
);

MemoizedIncompleteReportTableRow.displayName = "IncompleteReportTableRow";

export default MemoizedIncompleteReportTableRow;
